"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * A function with ONE permanent identity that always runs the LATEST `fn`.
 *
 * WHY THIS EXISTS: hooks in this app adapt a query / state setter into the shape
 * their consumers want and return handlers from it —
 *
 *   return { data, refetch: () => query.refetch(), loadMore: () => setPage((p) => p + 1) };
 *
 * Each inline arrow is a NEW function on every render. Consumers then list the
 * handler as a dependency (`useCallback(..., [refetch])`, `useEffect(..., [refetch])`,
 * a `React.memo` prop), so that one arrow silently re-ran every consumer's effect
 * and rebuilt every consumer's callback on every render. The allocation and the
 * dependency array live in different files, so neither looks wrong on its own.
 * See `scripts/validate-memo-dep-arrays.mjs` (check `unstable-hook-return-field`).
 *
 * Use this to return a handler from a hook:
 *
 *   const refetch = useStableCallback(() => query.refetch());
 *   return { data: query.data, refetch };
 *
 * Prefer a plain `useCallback` when the dependencies are themselves stable
 * (`[setPage]`, `[query.refetch]`) — it needs no ref. Reach for this when the
 * handler closes over something that legitimately changes every render (a fresh
 * `queries` array from `useQueries`, a `request` object) and you do not want that
 * churn to leak to callers.
 *
 * The latest `fn` is installed in an effect, so it is current for every call that
 * happens after commit (event handlers, effects, timers) — which is every
 * legitimate caller of a returned handler. Do not call the result DURING render.
 */
export function useStableCallback<Args extends unknown[], R>(
  fn: (...args: Args) => R,
): (...args: Args) => R {
  const latest = useRef(fn);
  useEffect(() => {
    latest.current = fn;
  });
  return useCallback((...args: Args) => latest.current(...args), []);
}
